import { type AnalyticsDailyRow, type AnalyticsDevice, type AnalyticsSessionRow } from '@carsfinders/contracts';

import { t } from '../../lib/i18n';

/**
 * Display helpers for «Аналітика». Every date on this screen is Europe/Kyiv —
 * the rollup cuts days on the Kyiv midnight (spec §5), so a UTC "today" would
 * disagree with the server's own today-row for three hours every night.
 */
export const KYIV_TIME_ZONE = 'Europe/Kyiv';

/** «0 с», «1 хв 5 с», «4 хв», «1 год 1 хв» — a session's length, never negative. */
export function formatDuration(totalSeconds: number): string {
  const sec = Math.max(0, Math.floor(Number.isFinite(totalSeconds) ? totalSeconds : 0));
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (h > 0) {
    return m > 0
      ? t('admin.analytics.duration.hm', { h, m })
      : t('admin.analytics.duration.h', { h });
  }
  if (m > 0) {
    return s > 0
      ? t('admin.analytics.duration.ms', { m, s })
      : t('admin.analytics.duration.m', { m });
  }
  return t('admin.analytics.duration.s', { s });
}

type Parts = Partial<Record<Intl.DateTimeFormatPartTypes, string>>;

function kyivParts(date: Date, options: Intl.DateTimeFormatOptions): Parts {
  const parts: Parts = {};
  for (const part of new Intl.DateTimeFormat('en-GB', {
    ...options,
    timeZone: KYIV_TIME_ZONE,
    hourCycle: 'h23',
  }).formatToParts(date)) {
    parts[part.type] = part.value;
  }
  return parts;
}

function toDate(value: string | Date): Date | null {
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** «13.10 00:30» — the admin's short day + clock, in Kyiv time. */
export function formatKyivDateTime(value: string | Date): string {
  const date = toDate(value);
  if (!date) return '—';
  const p = kyivParts(date, {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
  return `${p.day}.${p.month} ${p.hour}:${p.minute}`;
}

/** «14:22:05» — a page view inside an expanded session, in Kyiv time. */
export function formatKyivClock(value: string | Date): string {
  const date = toDate(value);
  if (!date) return '—';
  const p = kyivParts(date, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  return `${p.hour}:${p.minute}:${p.second}`;
}

/** Today's calendar day in Kyiv, `YYYY-MM-DD` — the API's `IsoDay`. */
export function kyivToday(now: Date = new Date()): string {
  const p = kyivParts(now, { year: 'numeric', month: '2-digit', day: '2-digit' });
  return `${p.year}-${p.month}-${p.day}`;
}

/** `YYYY-MM-DD` shifted by whole days (calendar arithmetic, no time zone). */
export function shiftIsoDay(day: string, delta: number): string {
  const [y, m, d] = day.split('-').map(Number);
  const date = new Date(Date.UTC(y!, m! - 1, d! + delta));
  return date.toISOString().slice(0, 10);
}

/** The `from`/`to` of the last `days` Kyiv days, today included. */
export function periodRange(days: number, today: string = kyivToday()): { from: string; to: string } {
  return { from: shiftIsoDay(today, -(days - 1)), to: today };
}

/** «13 жовт» — a chart axis / tooltip day. */
export function formatDayShort(day: string): string {
  const [y, m, d] = day.split('-').map(Number);
  const date = new Date(Date.UTC(y!, m! - 1, d!));
  return new Intl.DateTimeFormat('uk-UA', { day: 'numeric', month: 'short', timeZone: 'UTC' })
    .format(date)
    .replace(/\.$/, '');
}

/** Every day `from..to`, filled from `days` and zeroed where the server had no row. */
export function denseDays(days: readonly AnalyticsDailyRow[], from: string, to: string): AnalyticsDailyRow[] {
  const byDate = new Map(days.map((row) => [row.date, row]));
  const out: AnalyticsDailyRow[] = [];
  for (let day = from; day <= to; day = shiftIsoDay(day, 1)) {
    out.push(
      byDate.get(day) ?? {
        date: day,
        app: 'all',
        sessions: 0,
        pageViews: 0,
        uniqueVisitors: 0,
        uniqueUsers: 0,
        newVisitors: 0,
        avgSessionSec: 0,
      },
    );
  }
  return out;
}

export type PluralNoun = 'sessions' | 'unique' | 'registered';

const PLURAL_RULES = new Intl.PluralRules('uk-UA');

/** «1 захід», «3 заходи», «5 заходів» — Ukrainian plural forms from the catalogue. */
export function pluralCount(noun: PluralNoun, n: number): string {
  const rule = PLURAL_RULES.select(n);
  const form = rule === 'one' || rule === 'few' ? rule : 'many';
  return t(`admin.analytics.plural.${noun}.${form}`, { n });
}

/** «Відвідувач #abcd1234» — an anonymous visitor, by the first 8 chars of the id. */
export function visitorLabel(visitorId: string): string {
  return t('admin.analytics.sessions.visitor', { id: visitorId.slice(0, 8) || '—' });
}

/** The person behind a session: name, else email, else the user id. */
export function sessionUserLabel(user: NonNullable<AnalyticsSessionRow['user']>): string {
  return user.name?.trim() || user.email?.trim() || user.id;
}

/**
 * «Chrome Mobile» → «Chrome»: the row's device icon already says "mobile", and
 * the suffix is what pushed the OS out of the column.
 */
export function shortBrowser(browser: string): string {
  return browser.replace(/\s+Mobile(\s+(Safari|WebView))?$/i, '').trim() || browser;
}

function deviceParts(
  row: Pick<AnalyticsSessionRow, 'device' | 'browser' | 'os'>,
  browser: (b: string) => string,
): string {
  const parts = [row.browser ? browser(row.browser) : null, row.os].filter(
    (part): part is string => Boolean(part),
  );
  return parts.length > 0 ? parts.join(' · ') : deviceClassLabel(row.device);
}

/** «Chrome · Android» (short browser name), else the device class («Телефон»). */
export function deviceLabel(row: Pick<AnalyticsSessionRow, 'device' | 'browser' | 'os'>): string {
  return deviceParts(row, shortBrowser);
}

/** The unshortened label, for the cell's `title`. */
export function deviceFullLabel(row: Pick<AnalyticsSessionRow, 'device' | 'browser' | 'os'>): string {
  return deviceParts(row, (b) => b);
}

export function deviceClassLabel(device: AnalyticsDevice): string {
  return t(`admin.analytics.device.${device}`);
}

/** referrer host, else `utm_source`, else «прямий». */
export function sourceLabel(row: Pick<AnalyticsSessionRow, 'referrerHost' | 'utmSource'>): string {
  return row.referrerHost || row.utmSource || t('admin.analytics.sessions.direct');
}
